import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/** The width below which a menu is a bottom sheet. Keep the app stylesheet's media query in step. */
const SHEET_QUERY = "(width < 60rem)";

/** How far a finger must travel down before a touch on the sheet is a drag rather than a tap, in px. */
const DRAG_SLOP = 10;

/**
 * A button that opens a small panel of actions: the account menu, an item's
 * overflow. On a phone the same panel becomes a sheet from the bottom edge
 * over a scrim (that is CSS, in each app's stylesheet), because a popover anchored to a
 * 44px button at the bottom of a phone has nowhere good to go.
 *
 * As a sheet it holds the page still behind it and can be swiped down to
 * close, like a phone's own sheets.
 *
 * Closes on Escape, on a tap outside, and whenever an action inside calls
 * `close`, which every action should: each one either navigates or finishes.
 * Focus returns to the button on Escape so a keyboard is not dropped at the
 * top of the page.
 */
export function Menu({
  label,
  button,
  buttonClassName,
  placement = "down",
  align = "end",
  children,
}: {
  /** The button's accessible name, for when its visible content is only an icon or an avatar. */
  label: string;
  button: ReactNode;
  buttonClassName?: string;
  placement?: "up" | "down";
  align?: "start" | "end";
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();

  // As a sheet, the page behind stays put: without this a finger on the scrim
  // scrolled the page under it. The class is what app.css locks on, and it
  // only does so at sheet widths.
  useEffect(() => {
    if (!open) return;
    document.documentElement.classList.add("menu-open");
    return () => document.documentElement.classList.remove("menu-open");
  }, [open]);

  // And it can be swiped down to close, the way a phone's own sheets go: a
  // drag that starts while the sheet is scrolled to its top moves the sheet,
  // and one that goes far enough (or fast enough) closes it; a short one
  // springs back. Touch events rather than pointer events, because only a
  // non-passive touchmove can stop iOS from scrolling at the same time.
  useEffect(() => {
    const sheet = panel.current;
    if (!open || !sheet || !window.matchMedia(SHEET_QUERY).matches) return;
    let startY = 0;
    let startAt = 0;
    let dragging = false;
    let offset = 0;

    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      startY = event.touches[0]!.clientY;
      startAt = event.timeStamp;
      dragging = false;
      offset = 0;
    };
    const onMove = (event: TouchEvent) => {
      const dy = event.touches[0]!.clientY - startY;
      // A tap always drifts a pixel or two; only a real pull is a drag, or the
      // touch is cancelled and the tap never becomes a click.
      if (!dragging && (dy <= DRAG_SLOP || sheet.scrollTop > 0)) return;
      dragging = true;
      offset = Math.max(0, dy);
      event.preventDefault();
      sheet.style.transition = "none";
      sheet.style.transform = `translateY(${offset}px)`;
    };
    const onEnd = (event: TouchEvent) => {
      if (!dragging) return;
      dragging = false;
      const fast = offset / Math.max(1, event.timeStamp - startAt) > 0.5;
      sheet.style.transition = "";
      if (offset > Math.min(120, sheet.offsetHeight / 3) || (fast && offset > 24)) {
        setOpen(false);
        return;
      }
      sheet.style.transform = "";
    };

    sheet.addEventListener("touchstart", onStart, { passive: true });
    sheet.addEventListener("touchmove", onMove, { passive: false });
    sheet.addEventListener("touchend", onEnd);
    sheet.addEventListener("touchcancel", onEnd);
    return () => {
      sheet.removeEventListener("touchstart", onStart);
      sheet.removeEventListener("touchmove", onMove);
      sheet.removeEventListener("touchend", onEnd);
      sheet.removeEventListener("touchcancel", onEnd);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={root}>
      <button
        ref={trigger}
        type="button"
        className={buttonClassName}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {button}
      </button>
      {open && (
        <>
          <div className="menu-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
          <div ref={panel} id={id} className={`menu-panel menu-${placement} menu-${align}`}>
            {children(() => setOpen(false))}
          </div>
        </>
      )}
    </div>
  );
}

/** One row in a menu: an icon and a word, or a destructive one in red. */
export function MenuItem({
  icon,
  children,
  onClick,
  danger = false,
  disabled = false,
}: {
  icon?: ReactNode;
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={danger ? "menu-item menu-item-danger" : "menu-item"}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
